import React from 'react';
import Tile from '../../../app/common/components/Tile';

import Dropdown from '../../../app/common/components/Dropdown';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import TransactionTable from './transactionTable/TransactionTable';
import * as rewardsTransactionsHistoryStyle from './rewardsTransactionsHistoryStyles';
import { useDispatch, useSelector } from 'react-redux';
import { rewardsTransactionsHistoryActions } from './redux/rewardsTransactionsHistoryActions';
import { TransactionTableNavMenu } from './transactionTable/components/TransactionTableNavMenu';
import { selectAppConfig } from '../RewardsClub';
import {
    todaysDate,
    buildDatePeriodList,
    showPointsEarntSpentSummary,
    selectRewardsTransactionsHistory,
    showCashEarntSpentSummary,
} from './RewardsTransactionsHistoryHelper';

interface IRewardsTransactionsHistoryProps {
    cashbackCurrency: string | undefined;
    pointsCurrency: string;
    isSuperPointsAvailable: boolean;
}

const DATA_QA_ID = 'rewards-transaction-history';

export const RewardsTransactionsHistory: React.FC<IRewardsTransactionsHistoryProps> = ({
    cashbackCurrency,
    pointsCurrency,
    isSuperPointsAvailable,
}: IRewardsTransactionsHistoryProps) => {
    const dispatch = useDispatch();

    React.useEffect(() => {
        // when cashbackCurrency and points currency are changed,
        // assume we are selecting todays date

        // if GPP is down then donot call transactions api
        if (isSuperPointsAvailable) {
            if (showCashbackAndPoints && cashbackCurrency) {
                changeSelectedPeriod(todaysDate, cashbackCurrency);
            } else if (pointsCurrency) {
                changeSelectedPeriod(todaysDate, pointsCurrency);
            }
        }
        // set first selected currency as cashback when cashback is available.
        setDatePeriod(todaysDate);

        const defaultSelectedCurrency =
            showCashbackAndPoints && cashbackCurrency ? cashbackCurrency : pointsCurrency;
        setSelectedCurrency(defaultSelectedCurrency);
        //eslint-disable-next-line
    }, [cashbackCurrency, pointsCurrency, isSuperPointsAvailable]);

    const { pointsEarned, pointsSpent, error, fetching, history } = useSelector(
        selectRewardsTransactionsHistory,
    );

    const [datePeriod, setDatePeriod] = React.useState<string>(todaysDate);
    const [datePeriodList] = React.useState<string[]>(buildDatePeriodList());

    const {
        presentationConfig: {
            showSections: { cashbackAndPoints: showCashbackAndPoints },
        },
        locale: selectedLocale,
    } = useSelector(selectAppConfig);

    const defaultSelectedCurrency =
        showCashbackAndPoints && cashbackCurrency ? cashbackCurrency : pointsCurrency;
    const [selectedCurrency, setSelectedCurrency] = React.useState<string>(defaultSelectedCurrency);

    const changeSelectedPeriod = (newDatePeriod: string, currency: string) => {
        const [year, month] = newDatePeriod.split('/');
        dispatch(rewardsTransactionsHistoryActions(year, month, currency));
    };

    const pointsMenuItem = {
        buttonText: TranslationHelpers.getFormattedMessage('points.points'),
        onClick: () => {
            setSelectedCurrency(pointsCurrency);
            changeSelectedPeriod(datePeriod, pointsCurrency);
        },
        selected: selectedCurrency === pointsCurrency,
        uniqueKey: pointsCurrency,
    };
    const tableMenuItems =
        showCashbackAndPoints && cashbackCurrency !== undefined
            ? [
                  {
                      buttonText: TranslationHelpers.getFormattedMessage('points.cashback.cash'),
                      onClick: () => {
                          setSelectedCurrency(cashbackCurrency);
                          changeSelectedPeriod(datePeriod, cashbackCurrency);
                      },
                      selected: selectedCurrency === cashbackCurrency,
                      uniqueKey: cashbackCurrency,
                  },
                  pointsMenuItem,
              ]
            : [pointsMenuItem];

    return (
        <Tile
            localStyles={
                isSuperPointsAvailable
                    ? rewardsTransactionsHistoryStyle.RewardsTransactionsHistoryWrapper
                    : rewardsTransactionsHistoryStyle.RewardsTransactionsHistoryWrapperOpacity
            }
        >
            <rewardsTransactionsHistoryStyle.HistoryTopContainer>
                {tableMenuItems.length > 1 ? (
                    <>
                        <rewardsTransactionsHistoryStyle.TopButtonsContainer>
                            <rewardsTransactionsHistoryStyle.RewardButtonContainer
                                data-qa-id={DATA_QA_ID + '-rewards-button-menu'}
                            >
                                <TransactionTableNavMenu
                                    items={tableMenuItems}
                                    disabled={!isSuperPointsAvailable}
                                />
                            </rewardsTransactionsHistoryStyle.RewardButtonContainer>
                        </rewardsTransactionsHistoryStyle.TopButtonsContainer>
                        <rewardsTransactionsHistoryStyle.Title>
                            {TranslationHelpers.getFormattedMessage('points.history')}
                        </rewardsTransactionsHistoryStyle.Title>
                    </>
                ) : (
                    <>
                        <rewardsTransactionsHistoryStyle.TitleNoTableNavButtons>
                            {TranslationHelpers.getFormattedMessage('points.history')}
                        </rewardsTransactionsHistoryStyle.TitleNoTableNavButtons>
                    </>
                )}

                <rewardsTransactionsHistoryStyle.DropdownContainer>
                    <rewardsTransactionsHistoryStyle.DropdownSelector>
                        <Dropdown
                            key={pointsCurrency}
                            items={datePeriodList}
                            defaultSelected={datePeriodList[0]}
                            onChange={(selectedItem: string) => {
                                const formattedDate = selectedItem.replace('-', '/');
                                changeSelectedPeriod(formattedDate, selectedCurrency);
                                setDatePeriod(formattedDate);
                            }}
                            qaId={'pointsHistory'}
                            disabled={!isSuperPointsAvailable}
                        />
                    </rewardsTransactionsHistoryStyle.DropdownSelector>
                </rewardsTransactionsHistoryStyle.DropdownContainer>
            </rewardsTransactionsHistoryStyle.HistoryTopContainer>
            {pointsEarned !== null &&
                pointsSpent !== null &&
                history !== null &&
                history.length > 0 &&
                (selectedCurrency === cashbackCurrency ? (
                    <rewardsTransactionsHistoryStyle.EarntSpentSummaryContainer>
                        {showCashEarntSpentSummary(pointsEarned, pointsSpent, selectedLocale)}
                    </rewardsTransactionsHistoryStyle.EarntSpentSummaryContainer>
                ) : (
                    <rewardsTransactionsHistoryStyle.EarntSpentSummaryContainer>
                        {showPointsEarntSpentSummary(pointsEarned, pointsSpent)}
                    </rewardsTransactionsHistoryStyle.EarntSpentSummaryContainer>
                ))}

            {fetching && <rewardsTransactionsHistoryStyle.FetchingTransactions />}

            {error && (
                <rewardsTransactionsHistoryStyle.ErrorMessage
                    data-qa-id={DATA_QA_ID + '-fetch-error'}
                >
                    {TranslationHelpers.getFormattedMessage('points.no_fetch')}
                </rewardsTransactionsHistoryStyle.ErrorMessage>
            )}

            {history ? (
                history.length === 0 ? (
                    <rewardsTransactionsHistoryStyle.ErrorMessage
                        data-qa-id={DATA_QA_ID + '-no-history'}
                    >
                        {TranslationHelpers.getFormattedMessage('points.no_history')}
                    </rewardsTransactionsHistoryStyle.ErrorMessage>
                ) : (
                    <div>
                        <TransactionTable
                            transactions={history}
                            selectedLocale={selectedLocale}
                            pointsSelected={selectedCurrency !== cashbackCurrency}
                        />
                    </div>
                )
            ) : (
                <rewardsTransactionsHistoryStyle.ErrorMessage
                    data-qa-id={DATA_QA_ID + '-no-history'}
                >
                    {TranslationHelpers.getFormattedMessage('points.no_history')}
                </rewardsTransactionsHistoryStyle.ErrorMessage>
            )}
        </Tile>
    );
};
